import {
  Component,
  signal,
  ChangeDetectionStrategy,
  inject,
  OnInit,
  OnDestroy,
  effect,
} from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { TokenRefreshService } from './services/token-refresh.service';
import { AuthService } from './services/auth.service';
import { I18nStore } from './core/features/translations';
import { PortalUiService } from './services/portal-ui.service';
import { UserRoleService } from './services/user-role.service';
import { resolveSchoolFromUrl } from './core/config/school-logos.config';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, CdkScrollable],
  templateUrl: './app.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App implements OnInit, OnDestroy {
  private readonly tokenRefreshService = inject(TokenRefreshService);
  private readonly authService = inject(AuthService);
  private readonly i18n = inject(I18nStore); // Initialize I18n Store
  private readonly portalUi = inject(PortalUiService);
  private readonly userRoleService = inject(UserRoleService);

  constructor() {
    // Effect to apply font scaling
    effect(() => {
      const scale = this.portalUi.fontSizeScale();
      document.documentElement.style.setProperty('--font-adjustment', `${scale}px`);
    });

    // Effect to apply schools associated with the logged-in user (from teachers/managers or student degrees)
    effect(() => {
      // Only set if not already overridden by an external URL/LMS parameter
      const currentActive = this.portalUi.activeSchoolLogos();
      const schools = this.userRoleService.schools();
      if (schools && schools.length > 0 && !currentActive) {
        const shortnames = schools.map((s) => s.school_shortname).filter(Boolean);
        if (shortnames.length > 0) {
          this.portalUi.setActiveSchools(shortnames);
        }
      }
    });
  }

  readonly activeLogos = this.portalUi.activeSchoolLogos;

  readonly logos = [
    '/logo_vantage.svg',
    '/hso_logo.png',
    '/bvs_logo.png',
    '/sma_logo.png',
    '/ist_logo.png',
    '/pra_logo.png',
    '/apa_logo.png',
    '/mbs_logo.png',
    '/nov_logo.png',
    '/osd_logo.png',
    '/soa_logo.svg',
    // Duplicate for seamless loop
    '/logo_vantage.svg',
    '/hso_logo.png',
    '/bvs_logo.png',
    '/sma_logo.png',
    '/ist_logo.png',
    '/pra_logo.png',
    '/apa_logo.png',
    '/mbs_logo.png',
    '/nov_logo.png',
    '/osd_logo.png',
    '/soa_logo.svg',
    '/logo_vantage.svg',
  ];

  ngOnInit(): void {
    // 1. Check URL parameters, window origin or referrer for OpenOlat/LMS domain
    this.detectSchoolFromContext();

    // 2. Start token expiration monitoring if user is authenticated
    if (this.authService.isAuthenticated()) {
      this.tokenRefreshService.startMonitoring();
    }
  }

  private detectSchoolFromContext(): void {
    try {
      if (typeof window === 'undefined') return;

      const params = new URLSearchParams(window.location.search);
      const redirectUri = params.get('redirect_uri');
      const schoolParam = params.get('school');
      const referrer = document.referrer;
      const currentHost = window.location.hostname;

      const target = schoolParam || redirectUri || referrer || currentHost;
      const detectedSchool = resolveSchoolFromUrl(target);
      if (detectedSchool) {
        this.portalUi.setActiveSchools(detectedSchool);
      }
    } catch (e) {
      console.warn('Could not auto-detect school from URL context:', e);
    }
  }

  ngOnDestroy(): void {
    // Clean up token monitoring
    this.tokenRefreshService.stopMonitoring();
  }
}
